import React, { useState, useEffect } from "react";
import {
  Card,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Statistic,
  message,
  Spin,
  Table
} from "antd";
import { BarChartOutlined } from "@ant-design/icons";
import moment from "moment";
import clientApi from "../../clients/store/api";
import adminApi from "../../orders/store/api";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import { buildFirstLevelFilterRoute } from "modules/shared/PickupDropoff/helper";
import "./EstimateVsFinal.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "In Progress", value: "in-progress" },
  { label: "Completed", value: "completed" },
  { label: "Canceled", value: "canceled" },
  { label: "Failed", value: "failed" }
];

const EstimateVsFinal = () => {
  // Set default to last month (first day through last day)
  const getLastMonthRange = () => {
    const lastMonth = moment().subtract(1, "month");
    return [lastMonth.startOf("month").clone(), lastMonth.endOf("month").clone()];
  };

  const [dateRange, setDateRange] = useState(getLastMonthRange());
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(["completed"]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/billing-dashboard" },
    { text: "REPORTS", url: "/admin/billing/reports" },
    { text: "ESTIMATE VS FINAL", url: "/admin/billing/reports/estimate-vs-final" }
  ];

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await clientApi.getClientList({ pageSize: 10000 });
      const clientList = response.data || [];
      setClients(
        clientList.sort((a, b) => (a.clientName || "").localeCompare(b.clientName || ""))
      );
    } catch (error) {
      console.error("Error loading clients:", error);
      message.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const filterClients = (input, option) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const getOldestEstimate = (routeEstimates, type) => {
    if (!routeEstimates || routeEstimates.length === 0) return null;

    const filteredEstimates = routeEstimates.filter((est) => est.type === type);
    if (filteredEstimates.length === 0) return null;

    // Sort by createdAt ascending (oldest first)
    const sorted = filteredEstimates.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    return sorted[0];
  };

  const calculatePercentageDiff = (estimate, actual, isDriver = false) => {
    if (!estimate || estimate === 0) return null;

    const diff = ((actual - estimate) / estimate) * 100;

    // For driver: negative is good (less cost), positive is bad (more cost)
    // For client: positive is good (more revenue), negative is bad (less revenue)
    const isPositive = isDriver ? diff < 0 : diff > 0;

    return {
      value: Math.abs(diff).toFixed(1),
      isPositive,
      diff: diff
    };
  };

  const handleRunReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoading(true);
    try {
      // Build filter object matching RouteList pattern
      const rawFilter = {
        status: selectedStatus.length > 0 ? selectedStatus : [],
        pickupDateRange: null,
        closedDateRange: dateRange,
        startClosedDate: dateRange[0],
        endClosedDate: dateRange[1]
      };

      if (selectedClient !== "all") {
        rawFilter.clientId = selectedClient;
      }

      // Build the processed filter string array (same as RouteList)
      const processedFilter = buildFirstLevelFilterRoute(rawFilter);

      const params = {
        group: "flexio",
        page: 1,
        pageSize: 10000,
        filter: processedFilter,
        pickupFilter: rawFilter
      };

      console.log("=== REPORT PARAMETERS ===");
      console.log(
        "Date Range:",
        dateRange[0].format("YYYY-MM-DD"),
        "to",
        dateRange[1].format("YYYY-MM-DD")
      );
      console.log("Selected Client:", selectedClient);
      console.log("Raw Filter:", rawFilter);
      console.log("Processed Filter:", processedFilter);
      console.log("API Params:", params);

      const response = await adminApi.getRouteList(params);
      const routes = response.data || [];

      console.log("=== ROUTES FETCHED ===");
      console.log("Total routes:", routes.length);

      if (routes.length > 0) {
        console.log("First route sample:", {
          id: routes[0].id,
          completedDatetime: routes[0].completedDatetime,
          startDate: routes[0].startDate,
          status: routes[0].status,
          clientId: routes[0].clientId,
          clientName: routes[0].client?.clientName,
          hasRouteEstimates: !!routes[0].routeEstimates,
          routeEstimatesCount: routes[0].routeEstimates?.length || 0
        });

        // Check actual completed dates in the fetched routes
        const completedDates = routes
          .map((r) => r.completedDatetime)
          .filter((d) => d)
          .slice(0, 5);
        console.log("Sample completed dates:", completedDates);
      }

      // Calculate summary data
      let totalClientEstimate = 0;
      let totalClientFinal = 0;
      let totalDriverEstimate = 0;
      let totalDriverFinal = 0;
      const clientSummaries = {};

      routes.forEach((route, index) => {
        if (index === 0) {
          console.log("First route details:", {
            routeEstimates: route.routeEstimates,
            clientFinalPrice: route.clientFinalPrice,
            driverFinalPrice: route.driverFinalPrice,
            client: route.client
          });
        }

        const clientEstimate = getOldestEstimate(route.routeEstimates, "client_estimate");
        const driverEstimate = getOldestEstimate(route.routeEstimates, "driver_estimate");

        if (index === 0) {
          console.log("Oldest estimates:", { clientEstimate, driverEstimate });
        }

        const clientEstimatePrice = clientEstimate
          ? parseFloat(clientEstimate.estimatePrice) || 0
          : 0;
        const driverEstimatePrice = driverEstimate
          ? parseFloat(driverEstimate.estimatePrice) || 0
          : 0;
        const clientFinalPrice = parseFloat(route.clientFinalPrice) || 0;
        const driverFinalPrice = parseFloat(route.driverFinalPrice) || 0;

        if (index === 0) {
          console.log("Prices:", {
            clientEstimatePrice,
            driverEstimatePrice,
            clientFinalPrice,
            driverFinalPrice
          });
        }

        totalClientEstimate += clientEstimatePrice;
        totalClientFinal += clientFinalPrice;
        totalDriverEstimate += driverEstimatePrice;
        totalDriverFinal += driverFinalPrice;

        // Group by client
        if (!clientSummaries[route.clientId]) {
          clientSummaries[route.clientId] = {
            clientId: route.clientId,
            clientName:
              route.client?.clientName || route.client?.name || "Unknown Client",
            routeCount: 0,
            clientEstimate: 0,
            clientFinal: 0,
            driverEstimate: 0,
            driverFinal: 0
          };
        }

        clientSummaries[route.clientId].routeCount += 1;
        clientSummaries[route.clientId].clientEstimate += clientEstimatePrice;
        clientSummaries[route.clientId].clientFinal += clientFinalPrice;
        clientSummaries[route.clientId].driverEstimate += driverEstimatePrice;
        clientSummaries[route.clientId].driverFinal += driverFinalPrice;
      });

      const clientSummariesArray = Object.values(clientSummaries).sort((a, b) =>
        a.clientName.localeCompare(b.clientName)
      );

      setReportData({
        totalRoutes: routes.length,
        totalClientEstimate,
        totalClientFinal,
        totalDriverEstimate,
        totalDriverFinal,
        clientSummaries: clientSummariesArray,
        dateRange: `${dateRange[0].format("MMM D, YYYY")} - ${dateRange[1].format(
          "MMM D, YYYY"
        )}`,
        uniqueClients: Object.keys(clientSummaries).length
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const renderPercentageBadge = (estimate, actual, isDriver = false) => {
    const result = calculatePercentageDiff(estimate, actual, isDriver);
    if (!result) return <span style={{ color: "#999" }}>N/A</span>;

    const color = result.isPositive ? "#52c41a" : "#f5222d";
    const sign = result.diff > 0 ? "+" : "-";

    return (
      <span style={{ color, fontWeight: "bold" }}>
        {sign}
        {result.value}%
      </span>
    );
  };

  const columns = [
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      fixed: "left",
      width: 200
    },
    {
      title: "# Routes",
      dataIndex: "routeCount",
      key: "routeCount",
      align: "center",
      width: 100
    },
    {
      title: "Client Estimate",
      dataIndex: "clientEstimate",
      key: "clientEstimate",
      align: "right",
      width: 140,
      render: (val) => `$${val.toFixed(2)}`
    },
    {
      title: "Client Final",
      dataIndex: "clientFinal",
      key: "clientFinal",
      align: "right",
      width: 140,
      render: (val) => `$${val.toFixed(2)}`
    },
    {
      title: "Client Variance",
      key: "clientVariance",
      align: "center",
      width: 140,
      render: (_, record) =>
        renderPercentageBadge(record.clientEstimate, record.clientFinal, false)
    },
    {
      title: "Driver Estimate",
      dataIndex: "driverEstimate",
      key: "driverEstimate",
      align: "right",
      width: 140,
      render: (val) => `$${val.toFixed(2)}`
    },
    {
      title: "Driver Final",
      dataIndex: "driverFinal",
      key: "driverFinal",
      align: "right",
      width: 140,
      render: (val) => `$${val.toFixed(2)}`
    },
    {
      title: "Driver Variance",
      key: "driverVariance",
      align: "center",
      width: 140,
      render: (_, record) =>
        renderPercentageBadge(record.driverEstimate, record.driverFinal, true)
    }
  ];

  return (
    <div className="estimate-vs-final-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="content-wrapper">
        <h2>Estimate vs Final Report</h2>
        <p style={{ marginBottom: 24, color: "#666" }}>
          Compare route estimates with final prices for clients and drivers
        </p>

        <Card title="Report Filters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Client</div>
              <Select
                showSearch
                value={selectedClient}
                onChange={setSelectedClient}
                filterOption={filterClients}
                style={{ width: "100%" }}
                loading={loadingClients}
                placeholder="Select client"
              >
                <Option value="all">All Clients</Option>
                {clients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.clientName}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
              <Select
                mode="multiple"
                allowClear
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ width: "100%" }}
                placeholder="Select status"
                options={STATUS_OPTIONS}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<BarChartOutlined />}
                onClick={handleRunReport}
                loading={loading}
                style={{ width: "100%" }}
              >
                Run Report
              </Button>
            </Col>
          </Row>
        </Card>

        {reportData && (
          <>
            <Card title="Summary" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={4}>
                  <Statistic
                    title="Date Range"
                    value={reportData.dateRange}
                    valueStyle={{ fontSize: 14 }}
                  />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic title="# of Clients" value={reportData.uniqueClients} />
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Statistic title="# of Routes" value={reportData.totalRoutes} />
                </Col>
              </Row>

              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <h4>Client Totals</h4>
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Estimate Price"
                    value={reportData.totalClientEstimate}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Final Price"
                    value={reportData.totalClientFinal}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ paddingTop: 16 }}>
                    <strong>Variance: </strong>
                    {renderPercentageBadge(
                      reportData.totalClientEstimate,
                      reportData.totalClientFinal,
                      false
                    )}
                  </div>
                </Col>
              </Row>

              <div style={{ marginTop: 24, marginBottom: 16 }}>
                <h4>Driver Totals</h4>
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Estimate Price"
                    value={reportData.totalDriverEstimate}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Final Price"
                    value={reportData.totalDriverFinal}
                    precision={2}
                    prefix="$"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ paddingTop: 16 }}>
                    <strong>Variance: </strong>
                    {renderPercentageBadge(
                      reportData.totalDriverEstimate,
                      reportData.totalDriverFinal,
                      true
                    )}
                  </div>
                </Col>
              </Row>
            </Card>

            {reportData.clientSummaries.length > 1 && (
              <Card title="Per-Client Summary">
                <Table
                  columns={columns}
                  dataSource={reportData.clientSummaries}
                  rowKey="clientId"
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} clients`
                  }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}
          </>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>Generating report...</div>
          </div>
        )}

        {!reportData && !loading && (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
              <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <h3>Estimate vs Final Report</h3>
              <p>Select filters and click Run Report to view the data.</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EstimateVsFinal;
